import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { getToken } from "../../software/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const EXT = { "image/png": "png", "image/jpeg": "jpg", "image/gif": "gif", "image/webp": "webp", "image/bmp": "bmp" };

export async function uploadBlob(blob) {
  const fd = new FormData(); fd.append("file", blob, `logo.${EXT[blob.type] || "png"}`);
  const r = await fetch(`${BACKEND}/api/uploads`, { method: "POST", headers: { Authorization: `Bearer ${getToken()}` }, body: fd });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || "Logo upload failed");
  return /^https?:/.test(d.url) ? d.url : `${BACKEND}${d.url}`;
}

// Outlook/Word markup -> clean email HTML (keeps inline styles, links, tables, images)
export function cleanEmailHtml(html) {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const walk = (n) => [...n.childNodes].forEach((c) => {
    if (c.nodeType === 8) { c.remove(); return; }
    if (c.nodeType !== 1) return;
    const tag = c.tagName.toLowerCase();
    if (["style", "script", "meta", "link", "xml", "title", "head"].includes(tag)) { c.remove(); return; }
    if (tag.includes(":")) { c.replaceWith(...c.childNodes); return; }
    [...c.attributes].forEach((a) => {
      const k = a.name.toLowerCase();
      if (k === "style") {
        const st = a.value.split(";").filter((d) => d.trim() && !/^\s*(mso-|tab-stops|text-autospace|font-feature)/i.test(d)).join(";");
        if (st) c.setAttribute("style", st); else c.removeAttribute("style");
      } else if (!["href", "src", "alt", "width", "height", "colspan", "rowspan", "cellpadding", "cellspacing", "border", "align", "valign", "bgcolor", "target", "title"].includes(k)) c.removeAttribute(a.name);
    });
    walk(c);
  });
  walk(doc.body);
  return doc.body.innerHTML.replace(/<p>\s*(&nbsp;)?\s*<\/p>/g, "").trim();
}

// re-host every logo so it shows for the person receiving the email
async function hostImages(html, files) {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const imgs = [...doc.querySelectorAll("img")];
  const spare = [...files];
  let missing = 0;
  for (const img of imgs) {
    const src = img.getAttribute("src") || "";
    try {
      if (/^data:image\//.test(src)) img.setAttribute("src", await uploadBlob(await (await fetch(src)).blob()));
      else if (!/^https?:/.test(src)) {
        const f = spare.shift();
        if (f) img.setAttribute("src", await uploadBlob(f)); else { img.setAttribute("src", ""); img.setAttribute("alt", ""); img.setAttribute("title", "Click to add this logo"); missing += 1; }
      }
    } catch (e) { toast.error(e.message); missing += 1; }
  }
  return { html: doc.body.innerHTML, missing };
}

export const htmlToText = (html) => {
  const d = new DOMParser().parseFromString(String(html || "").replace(/<br\s*\/?>/gi, "\n").replace(/<\/(p|div|tr|h\d|li)>/gi, "\n"), "text/html");
  return (d.body.textContent || "").replace(/\u00a0/g, " ").replace(/\n{3,}/g, "\n\n").trim();
};
export const isHtml = (s) => /<[a-z][\s\S]*>/i.test(String(s || ""));

/* Paste a signature/template straight from Outlook: logos, links and formatting are kept as HTML. */
export function HtmlPasteBox({ value, onChange, testid, placeholder = "Paste here from Outlook (Ctrl+V) — logos and links are kept", minH = "min-h-[140px]", showClear = true }) {
  const ref = useRef(null), fileInp = useRef(null), target = useRef(null);
  const [busy, setBusy] = useState(false), last = useRef(null);
  // only overwrite the box when the value changed from outside (tab switch, reset) — not while typing
  useEffect(() => { if (ref.current && value !== last.current) { ref.current.innerHTML = value || ""; last.current = value; } }, [value]);
  const emit = () => { const v = cleanEmailHtml(ref.current.innerHTML); if (v === last.current || v === cleanEmailHtml(last.current || "")) return; last.current = v; onChange(v); };

  const insertHtml = (html) => { ref.current.focus(); document.execCommand("insertHTML", false, html); emit(); };
  const onPaste = async (e) => {
    const cd = e.clipboardData, html = cd.getData("text/html"), files = [...cd.files].filter((f) => f.type.startsWith("image/"));
    e.preventDefault();
    setBusy(true);
    const t = toast.loading("Copying signature…");
    try {
      if (html) {
        const r = await hostImages(cleanEmailHtml(html.replace(/^[\s\S]*<!--StartFragment-->|<!--EndFragment-->[\s\S]*$/g, "")), files);
        insertHtml(r.html);
        toast[r.missing ? "warning" : "success"](r.missing ? `${r.missing} logo(s) could not be copied from Outlook — click the empty logo box to add the picture` : "Pasted with logos and links", { id: t, duration: 8000 });
      } else if (files.length) { insertHtml(`<img src="${await uploadBlob(files[0])}" alt="Logo"/>`); toast.success("Picture added", { id: t }); }
      else { insertHtml(cd.getData("text/plain").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/\n/g, "<br/>")); toast.dismiss(t); }
    } catch (err) { toast.error(err.message, { id: t }); } finally { setBusy(false); }
  };
  const onClick = (e) => { if (e.target.tagName === "IMG") { target.current = e.target; fileInp.current?.click(); } };
  const onFile = async (e) => {
    const f = e.target.files?.[0]; e.target.value = ""; if (!f || !target.current) return;
    try { target.current.setAttribute("src", await uploadBlob(f)); target.current.setAttribute("alt", "Logo"); target.current.removeAttribute("title"); emit(); toast.success("Logo replaced"); } catch (err) { toast.error(err.message); }
  };
  return (
    <div className="relative">
      <div ref={ref} contentEditable suppressContentEditableWarning onPaste={onPaste} onInput={emit} onBlur={emit} onClick={onClick}
        data-testid={testid} data-placeholder={placeholder}
        className={`html-paste-box ${minH} max-h-[360px] overflow-auto border border-slate-300 rounded bg-white p-3 text-[13px] outline-none focus:border-[#2f5fae] [&_img]:cursor-pointer [&_a]:text-blue-700 [&_a]:underline [&_img[src='']]:inline-block [&_img[src='']]:w-24 [&_img[src='']]:h-12 [&_img[src='']]:border-2 [&_img[src='']]:border-dashed [&_img[src='']]:border-amber-400 ${busy ? "opacity-60" : ""}`} />
      <input ref={fileInp} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid={`${testid}-file`} />
      <div className="mt-1 flex justify-between text-[10.5px] text-slate-400">
        <span>Click a logo to replace it.</span>
        {showClear && value && <button type="button" onClick={() => { if (window.confirm("Clear this and start again?")) onChange(""); }} data-testid={`${testid}-clear`} className="font-semibold text-slate-500 hover:underline">Clear</button>}
      </div>
    </div>
  );
}
